import Vue from 'vue';
import trainingResultService from '@/api/training-result-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultTrainingResultFormItem = () => ({});

export const getDefaultTrainingResultFilterParams = () => ({});

const state = {
  trainingResults: null,
  exams: [],
  trainingResultPagination: {},
  userTrainingResult: {},
  editedTrainingResult: {},
  trainingResultValidationErrors: {},
  trainingResultFilterParams: getDefaultTrainingResultFilterParams(),
  newTrainingResult: getDefaultTrainingResultFormItem(),
};

const getters = {};

const mutations = {
  SET_TRAINING_RESULTS(state, { data, current_page, per_page, total }) {
    state.trainingResults = data;
    state.trainingResultPagination = {
      current_page,
      per_page,
      total,
    };
  },
  
  SET_EXAMS(state, exams) {
    state.exams = exams;
  },

  SET_TRAINING_RESULT_FILTER_PARAMS(state, params) {
    state.trainingResultFilterParams = params;
  },

  SET_EDITED_TRAINING_RESULT(state, trainingResult) {
    state.trainingResultValidationErrors = {};
    state.userTrainingResult = JSON.parse(JSON.stringify(trainingResult));
    state.editedTrainingResult = JSON.parse(JSON.stringify(trainingResult));
  },

  STORE_TRAINING_RESULT(state, trainingResult) {
    state.trainingResults = [...state.trainingResults, trainingResult];
    state.trainingResultPagination.total += 1;
    state.trainingResultValidationErrors = {};
    state.newTrainingResult = getDefaultTrainingResultFormItem();
  },

  UPDATE_TRAINING_RESULT(state, trainingResult) {
    state.trainingResults = updateArrayItem(state.trainingResults, trainingResult);
  },

  DELETE_TRAINING_RESULT(state, trainingResult) {
    state.trainingResults = removeArrayItem(state.trainingResults, trainingResult);
    state.trainingResultPagination.total -= 1;
  },

  SET_TRAINING_RESULT_VALIDATION_ERRORS(state, trainingResultValidationErrors) {
    state.trainingResultValidationErrors = trainingResultValidationErrors;
  },

  RESET_TRAINING_RESULT_VALIDATION_ERRORS(state) {
    state.trainingResultValidationErrors = {};
  },

  CLEAR_TRAINING_RESULT_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.trainingResultValidationErrors, field);
  },
};

const actions = {
  fetchTrainingResults({ commit }, params) {
    commit('SET_TRAINING_RESULT_FILTER_PARAMS', params);
    return trainingResultService.getPage(params).then((res) => {
      commit('SET_TRAINING_RESULTS', res.data.trainingResults);
      commit('SET_EXAMS', res.data.exams);
      if (params.page !== state.trainingResultFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storeTrainingResult({ commit }, trainingResult) {
    return trainingResultService
      .create(trainingResult)
      .then((res) => {
        commit('STORE_TRAINING_RESULT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('training_result_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_TRAINING_RESULT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editTrainingResult({ state, commit }, trainingResultId) {
    const trainingResult = state.trainingResults?.find((c) => c.id === trainingResultId);
    if (trainingResult) {
      commit('SET_EDITED_TRAINING_RESULT', trainingResult);
      return Promise.resolve(trainingResult);
    }
    return trainingResultService.getById(trainingResultId).then((res) => {
      commit('SET_EDITED_TRAINING_RESULT', res.data);
      return res.data;
    });
  },

  updateTrainingResult({ commit }, trainingResult) {
    return trainingResultService
      .update(trainingResult)
      .then((res) => {
        commit('UPDATE_TRAINING_RESULT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('training_result_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_TRAINING_RESULT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearTrainingResultValidationErrors({ commit }, field) {
    commit('CLEAR_TRAINING_RESULT_VALIDATION_ERRORS', field);
  },

  deleteTrainingResult({ commit }, trainingResult) {
    openConfirmDialog({
      title: i18n.t('confirm_training_result_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      trainingResultService.delete(trainingResult).then(() => {
        commit('DELETE_TRAINING_RESULT', trainingResult);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('training_result_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
